// Thin client for the /assessments API.
//
// Errors carry `status`, with 0 meaning the request never reached the server
// (offline, DNS, CORS, timeout). The sync layer decides what each one means.

const API_URL = process.env.REACT_APP_API_URL;
const TIMEOUT_MS = 20000;

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function call(path, { method = 'GET', body } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('wmf_token')}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    throw new ApiError('Could not reach the server', 0);
  } finally {
    clearTimeout(timer);
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(data.msg || `Request failed (${response.status})`, response.status, data);
  }
  return data;
}

export const assessmentApi = {
  schema: (code, version) => call(`/assessments/schema/${code}${version ? `?version=${version}` : ''}`),
  list: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return call(`/assessments${qs ? `?${qs}` : ''}`);
  },
  get: (uuid) => call(`/assessments/${uuid}`),
  create: (payload) => call('/assessments', { method: 'POST', body: payload }),
  changes: (uuid, changes) => call(`/assessments/${uuid}/changes`, { method: 'POST', body: { changes } }),
  complete: (uuid) => call(`/assessments/${uuid}/complete`, { method: 'POST' }),
  reopen: (uuid) => call(`/assessments/${uuid}/reopen`, { method: 'POST' }),
  cancel: (uuid, reason) => call(`/assessments/${uuid}/cancel`, { method: 'POST', body: { reason } }),
  surveyStatus: (uuid) => call(`/assessments/${uuid}/survey-status`),
  surveyLink: (uuid) => call(`/assessments/${uuid}/survey-link`, { method: 'POST' }),
};

export const staffApi = {
  myStudents: () => call('/therapists/my-students'),
  therapists: () => call('/therapists'),
  therapistStudents: (therapistId) => call(`/therapists/${therapistId}/students`),
};
